import {
  RowData,
  RowDataReducerAction,
  RowDataReducerState,
  RowDataValueType,
  SetupMode,
} from "../../../../types";
import { TableHeaderProps } from "@cdkglobal/radial";
import RecordPage from "../record-page/RecordPage";
import InventoryBuildingCards from "../custom-renderers/inventory-buildings/InventoryBuildingCards";
import SetupTableContent from "./SetupTableContent";
import RecordDeletionDialog from "../table-actions/RecordDeletionDialog";
import { ColDef } from "ag-grid-community";
import ARMigrationDialog from "../table-actions-setup-based/ARMigrationDialog";
import COAFileUpload from "../custom-renderers/coa-file-upload/COAFileUpload";
import PricingMatrixFileUpload from "../custom-renderers/pricing-matrix/PricingMatrixFileUpload";
import { SCHEMA_TITLES } from "../../constants";

export interface SetupModeRendererProps {
  setupMode: SetupMode;
  effectiveGridFields: Set<string> | undefined;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
  // Table content
  languageSpecificColDefs: ColDef[];
  dataToDisplay: RowData[];
  tableHeaderProps: TableHeaderProps;
  gridFields: Set<string>;
  setupName: string;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  // Permissions
  isEditDisabled: boolean;
  isDeleteDisabled: boolean;
  isSetupActionsLocked: boolean;
  // Dialogs
  isRecordDeleteDialogOpen: boolean;
  isExportDialogOpen: boolean;
  isARMigrationDialogOpen: boolean;
  isFileUploadDialogOpen: boolean;
  setIsFileUploadDialogOpen: (isOpen: boolean) => void;
  // Row action handlers
  handleView: (rowData: RowData) => void;
  handleEdit: (rowData: RowData) => void;
  handleCopy: (rowData: RowData) => Promise<void>;
  handleDelete: (rowData: RowData) => void;
  // Render helper
  renderField: (
    fieldName: string,
    fieldValue: RowDataValueType,
    rowData: RowData,
  ) => React.ReactNode;
  getFieldDisplayValue: (
    fieldName: string,
    rowData: RowData,
  ) => RowDataValueType;
  headerLabel: string;
}

export default function SetupModeRenderer({
  setupMode,
  effectiveGridFields,
  rowDataReducerState,
  dispatch,
  languageSpecificColDefs,
  dataToDisplay,
  tableHeaderProps,
  gridFields,
  setupName,
  currentPage,
  setCurrentPage,
  isEditDisabled,
  isDeleteDisabled,
  isSetupActionsLocked,
  isRecordDeleteDialogOpen,
  isExportDialogOpen,
  isARMigrationDialogOpen,
  isFileUploadDialogOpen,
  setIsFileUploadDialogOpen,
  handleView,
  handleEdit,
  handleCopy,
  handleDelete,
  renderField,
  getFieldDisplayValue,
  headerLabel,
}: Readonly<SetupModeRendererProps>): JSX.Element {
  const { schema } = rowDataReducerState;

  switch (setupMode) {
    case "DASHBOARD_LEVEL_SETUP":
    case "DASHBOARD_TABBED_SETUP":
    case "SINGLE_RECORD_DASHBOARD_LEVEL_SETUP":
    case "SINGLE_FIELD_DASHBOARD_LEVEL_SETUP":
      if (!effectiveGridFields) {
        return (
          <RecordPage
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
          />
        );
      }
      break;

    case "INVENTORY_BUILDINGS_SETUP":
      return (
        <InventoryBuildingCards
          rowDataReducerState={rowDataReducerState}
          dataToDisplay={dataToDisplay}
          handleEdit={handleEdit}
          dispatch={dispatch}
        />
      );
  }

  return (
    <>
      <SetupTableContent
        rowDataReducerState={rowDataReducerState}
        languageSpecificColDefs={languageSpecificColDefs}
        dataToDisplay={dataToDisplay}
        tableHeaderProps={tableHeaderProps}
        renderField={renderField}
        getFieldDisplayValue={getFieldDisplayValue}
        handleView={handleView}
        handleEdit={handleEdit}
        handleCopy={handleCopy}
        handleDelete={handleDelete}
        isEditDisabled={isEditDisabled}
        isDeleteDisabled={isDeleteDisabled}
        isSetupActionsLocked={isSetupActionsLocked}
        gridFields={gridFields}
        setupName={setupName}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isExportDialogOpen={isExportDialogOpen}
        headerLabel={headerLabel}
      />

      {isRecordDeleteDialogOpen && (
        <RecordDeletionDialog
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
          schema={schema}
        />
      )}

      {isARMigrationDialogOpen && <ARMigrationDialog />}
      {setupName === SCHEMA_TITLES.CHART_OF_ACCOUNTS && (
        <COAFileUpload
          isOpen={isFileUploadDialogOpen}
          onClose={() => setIsFileUploadDialogOpen(false)}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      )}
      {setupName === SCHEMA_TITLES.PRICING_MATRIX && (
        <PricingMatrixFileUpload
          isOpen={isFileUploadDialogOpen}
          onClose={() => setIsFileUploadDialogOpen(false)}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      )}
    </>
  );
}
